import type { AskViewExecutiveOutcome } from './executive-outcomes';
import type { AskViewProgramOutcome } from './program-outcome-payload';
export * from './program-outcome-payload';
import type { AskViewPublishedOptions, AskViewRecoveryScenario } from './loop2-payload';
export * from './loop2-payload';
export * from './drill-read';
import type { AskViewHomeDecision, AskViewDecisionAccountability, AskViewPublishedAction,
  AskViewOperatingReview, AskViewDecisionAvailability, AskViewDecisionScopeCoverage, AskViewDecisionFinance }
  from './operating-review-payload';
export * from './operating-review-payload';
import type { AskHelixNumericBindingDto, AskHelixNumericBindingSourceDto } from '../../ask-helix';
export type { AskViewExecutiveOutcome } from './executive-outcomes';
import type { AskViewDriverTree, AskViewDriverTreeUnavailable } from './driver-tree-payload';
export type { AskViewDriverTree, AskViewDriverNode, AskViewDriverFigure,
  AskViewDriverShareUnavailable, AskViewDriverShareAdapterCode, AskViewDriverMappingUnavailable,
  AskViewDriverTreeUnavailable } from './driver-tree-payload';
export { ASK_DRIVER_MAPPING_UNAVAILABLE_CODES, ASK_DRIVER_SHARE_ADAPTER_CODES }
  from './driver-tree-payload';
/** THE VIEW CONTRACT (rail menu v2 build contract, SPEC.md §2 and §4, 2026-09-27) — the ONE
 * shared definition of a governed view's address and payload. The server's view registry
 * (`GET v1/views/:viewId`) produces it and the web canvas renders it; neither side redefines it.
 *
 * Every text field is filled from the view's §6 grammar, each slot bound to a typed finding;
 * no model writes any of it at runtime, and a sentence whose slot has no finding is omitted,
 * never defaulted. Figures live only in the exhibit, as declared exhibits over engine-bound
 * bindings, each exact figure placed once; narration may cite a figure in rounded short form.
 */
import type { ExecutiveLens } from '../../executive-findings-request';
import type { AskViewExecutivePanel } from './executive-panels';
import type {
  SynergyForecastCoverage,
  SynergyTurnedSignal,
  SynergyViewPanel,
} from '../contracts/synergy-views';
import type { SynergyFigure } from '../../synergy-risk';
import type { SynergyVerificationView } from '../../synergy-verification';
import type { DecisionLedgerSynergyDecisionTypeDto } from '../../decision-ledger';
import type { AskViewItBridgeBlock } from './it-bridge-payload';
export * from './it-bridge-payload';
import type { AskViewDealCaseBlock } from './deal-case-payload';
import type { AskViewRecoveryOptionsBlock } from './recovery-options-payload';
export * from './recovery-options-payload';
export * from './recovery-outcome-payload';
import type { AskViewCommitmentBlock } from './commitment-payload';
import type { LessonsViewBlock } from '../../lessons/read-contract';
export * from './commitment-payload';
import type { BehindVisual, WarningsVisual } from './rail-risk-visual';
export * from './rail-risk-visual';
import type { AskViewComparisonPresentation } from './comparison-payload';
export * from './comparison-payload';
export * from './deal-case-payload';
export * from '../../figure-labels';

/** The rail's views (SPEC §3). `commitment` is reached from commitment links, not the rail. */
export const ASK_VIEW_IDS = [
  'deal-case',
  // The landing: what the canvas shows before any rail click (founder GO 2026-09-27 19:23 PT).
  'home',
  'thesis',
  'plan',
  'since',
  'behind',
  'warnings',
  'verified',
  'decisions',
  'scenarios',
  'lessons',
  'ev-sga',
  'ev-it',
  'ev-workforce',
  'commitment',
] as const;
export type AskViewId = (typeof ASK_VIEW_IDS)[number];

/** A day, `YYYY-MM-DD`. */
export type AskViewDay = string;

/**
 * Where a view is (SPEC §2.1). `dealId` is the deal's governed member ref; `asOf` pins the
 * snapshot the numbers are read at, so a link in a board pack keeps its numbers.
 */
export type AskViewAddress = Readonly<{
  dealId: string;
  asOf: AskViewDay;
  viewId: AskViewId;
  /** The view's own typed params (e.g. `commitmentId`, `tab`), by the names §6 declares. */
  params: Readonly<Record<string, string>>;
}>;

/** One breadcrumb (SPEC §2.2); every level but the last is a button. */
export type AskViewCrumb = Readonly<{ label: string; address?: AskViewAddress }>;

/** "Recommended next step" and "Why" (advisor voice; unique per view). */
export type AskViewBandAction = Readonly<{
  decisionType: DecisionLedgerSynergyDecisionTypeDto;
  scopeKind: 'synergy_commitment' | 'synergy_dependency';
  scopeId: string;
}>;

export type AskViewBand = Readonly<{
  next: string;
  why: string;
  action?: AskViewBandAction;
  /** The engine's published move behind `next` and its bound value, as Decide's step carries it. */
  publishedAction?: AskViewPublishedAction | null;
}>;
/** A link to another view, with the words on it. */
export type AskViewDrill = Readonly<{ label: string; address: AskViewAddress }>;
/** What the view cannot show yet, and what it would take (SPEC data statuses). */
export const ASK_VIEW_MISSING_KINDS = ['NEEDS DATA', 'NEEDS ENGINE', 'CONFLICT', 'DELIVERY GAP'] as const;
export const ASK_VIEW_BRIDGE_CONFLICT = {
  refused: 'MemberBridgeRefused',
  aggregate: 'AggregateBridgeRefused',
  excluded: 'MemberBridgeExcluded',
} as const;
export type AskViewMissing = Readonly<{
  what: string;
  kind: (typeof ASK_VIEW_MISSING_KINDS)[number];
  numericBindings?: readonly AskHelixNumericBindingDto[];
  reason?: { code: (typeof ASK_VIEW_BRIDGE_CONFLICT)[keyof typeof ASK_VIEW_BRIDGE_CONFLICT];
    commitmentRef?: string; refusalReason?: string;
    sourceRevisions?: AskHelixNumericBindingSourceDto['revisions'] };
}>;
/** One panel of an exhibit: a declared exhibit over its own published, engine-bound figures. */
export type AskViewPanel = SynergyViewPanel;

/**
 * The view's exhibit. Panels are drawn by the same exhibit registry as /ask answers. A view whose
 * figures come from a published screen (the at-risk and verification reads, figures with their
 * evidence) carries them as its typed `block` beside the panels; they are never re-minted as
 * engine bindings. A renderer that does not know a block still draws the panels.
 */
export type AskViewExhibit = Readonly<{
  kind: 'panels';
  panels: readonly AskViewPanel[];
  block?: AskViewBlock;
  /** Page-date options remain separate from a commitment's captured delivery block. */
  commitmentOptions?: AskViewPublishedOptions;
  driverTree?: AskViewDriverTree;
  itTakeout?: import('./driver-tree-payload').AskViewItTakeout;
  driverTreeUnavailable?: AskViewDriverTreeUnavailable;
  /** Wave C0: executive finding panels; present only with HELIX_EXECUTIVE_REASONING on. */
  executive?: readonly AskViewExecutivePanel[];
  executiveOutcomes?: readonly AskViewExecutiveOutcome[];
  /** The lens the server ordered the panels for; absent when none was applied. */
  executiveLens?: ExecutiveLens;
}>;

export type AskViewBlock =
  | AskViewRecoveryOptionsBlock
  | LessonsViewBlock
  | AskViewCommitmentBlock
  | AskViewItBridgeBlock
  | AskViewDealCaseBlock
  | import('./deal-case-payload').AskViewApprovedCaseBlock
  | AskViewThesisBlock
  | AskViewTurnedBlock
  | AskViewVerificationBlock
  | AskViewBehindBlock
  | AskViewWarningsBlock
  | AskViewDecisionsBlock
  | AskViewHomeBlock;

/**
 * `home` (founder GO 2026-09-27 19:23 PT, from the CEO / CFO / IMO / PE reviews): the landing,
 * composed server-side from the views that already exist (verified, decisions, since, plan) for
 * the address's program, plus one strip row per program. Every figure is a figure those views
 * already publish, carried as-is; every sentence is server-authored. The headline cites the
 * program's proven and committed figures; nothing else on the page repeats them except its
 * own strip row.
 */
/**
 * `home` v2 (founder approval 2026-09-27 22:02 PT of mock iteration 7): the landing for the SELECTED
 * program only. The verdict names the decision to push and its owner; talk[0] is the one-line
 * so-what. Every figure is one the underlying views already publish, carried as-is; every
 * sentence is server-authored. Positions on the hero bar are 0..1 shares, never displayed.
 */
export type AskViewHomeBlock = Readonly<{
  kind: 'home';
  shortfallsReconciled?: boolean; /** Server sentence when they do not add exactly (walk A). */ shortfallsNote?: string;
  overview?: import('../contracts/synergy-overview.v1').SynergyOverviewV1;
  board?: import('./board-payload').AskViewBoardBlock;
  decisionAvailability?: AskViewDecisionAvailability;
  scopeCoverage?: AskViewDecisionScopeCoverage;
  review?: AskViewOperatingReview;
  monthlyChange?: Readonly<{ before: string; change: SynergyFigure }>;
  labels?: Readonly<{ hero: string; fineprint: string; legend: readonly string[] }>;
  /** The open decision that protects the most (the one to push), or null when none is open. */
  decision: AskViewHomeDecision | null;
  /** The selected program's hero: ledger-verified / realized / committed, plus the landing. */
  program: AskViewHomeProgram | null;
  /** Where the program is heading: the estimated landing. Null when no forecast is published. */
  landing: AskViewHomeLanding | null;
  /** Up to three cards, in this order: thesis, root cause, what changed. Absent parts are omitted. */
  cards: readonly AskViewHomeCard[];
}>;

export type AskViewHomeLanding = Readonly<{
  periodBasis?: import('../../figure-period-basis').FigurePeriodBasis;
  planGap?: Readonly<{
    text: string;
    amountText?: string;
    label?: string;
    startShare: number;
    widthShare: number;
  }> | null;
  shortfall?: SynergyFigure | null;
  shortfallShare?: number | null;
  coverage?: SynergyForecastCoverage;
  /** "The estimate covers the 4 commitments in the plan; the 4 outside it cannot be forecast until …" */
  coverageNote?: string;
  cases?: Readonly<Record<'downside' | 'base' | 'upside', SynergyFigure>>;
  /** Bar positions on the committed scale, 0..1 (drawing only). Absent without cases. */
  positions?: Readonly<{ downside: number; base: number; upside: number }>;
  temporalBasis?: 'future' | 'observed';
  label: 'Estimated' | 'FY-end latest estimate' | 'Ledger-verified run-rate';
  /** "Lands at … by Dec 31, 2026 against … committed": the sentence without its figures. */
  text: string;
  /** The base case. */
  figure: SynergyFigure;
  /** The reader date the estimate lands by. */
  by: string;
  address: AskViewAddress;
}>;

/**
 * Reported realized against its split (#9936). Realized and the split (ledger-verified +
 * reported-not-verified) come from separate publications: only a 'reconciled' split is shown as
 * one; 'unreconciled' carries the bound residual and the server's sentence for it.
 */
export type AskViewHomeSplit =
  | Readonly<{ state: 'reconciled' }>
  | Readonly<{ state: 'unreconciled'; residual: SynergyFigure; text: string }>;

export type AskViewHomeProgram = Readonly<{
  sourceLine?: string;
  overlaySourceLine?: string;
  reportedNotVerified?: SynergyFigure | null;
  overlayNetVerified?: SynergyFigure | null;
  overlayRows?: readonly Readonly<{ label: string; figure: SynergyFigure | null }>[];
  /** Absent when realized or a part of its split is not measurable. */
  split?: AskViewHomeSplit;
  currentPlan?: SynergyFigure | null;
  planShare?: number | null;
  planAsOf?: string;
  dealId: string;
  /** The declared short name ("Phoenix"), else the label. */
  name: string;
  /** Ledger-verified run-rate (never the reported figure); null when the ledger has none. */
  proven: SynergyFigure | null;
  /** Realized (reported value capture), not an assertion that the ledger verified it. */
  realized: SynergyFigure | null;
  committed: SynergyFigure | null;
  /** Bar positions on the committed scale, 0..1 (drawing only; never displayed). */
  provenShare: number | null;
  realizedShare: number | null;
  address: AskViewAddress;
}>;

/** One "why" card under the hero. Title and text are server sentences (figures in compact form). */
export type AskViewHomeCard = Readonly<{
  kind: 'thesis' | 'cause' | 'changed' | 'deal-case';
  /** Governed figures cited by this card, copied with the producer's bindings. */
  figures?: readonly SynergyFigure[];
  pillars?: readonly Readonly<{ label: string; status: AskViewThesisStatus; statusNote?: string }>[];
  /** "Thesis", "Root cause", "What changed since Jul 31". */
  kicker: string;
  /** "3 of 4 pillars breaking". */
  title: string;
  /** One or two sentences. */
  text: string;
  /** Exact server-selected spans to emphasize without rewriting the sentence. */
  emphasis?: readonly string[];
  /** The view the card opens; its link reads that view's rail label. */
  address: AskViewAddress;
}>;

/**
 * Live update (founder 2026-09-27 20:38 PT): `GET v1/views/revision?dealId=&asOf=` answers the digest
 * of every published revision the program's views read. The open view re-fetches when it changes.
 */
export const ASK_VIEW_REVISION_PATH = 'v1/views/revision';
export type AskViewRevision = Readonly<{ dealId: string; asOf: string; revision: string }>;

export const ASK_VIEW_STATUS_LABELS = {
  on_track: 'On track',
  at_risk: 'At risk',
  off_track: 'Off track',
  not_yet_measurable: 'Not yet measurable',
  closed: 'Closed',
} as const;
export type AskViewThesisStatus = Exclude<keyof typeof ASK_VIEW_STATUS_LABELS, 'closed'>;
/** Published statuses plus the ungraded overlay class (#9599). */
export const ASK_VIEW_COMMITMENT_STATUS_LABELS = {
  on_track: ASK_VIEW_STATUS_LABELS.on_track,
  at_risk: ASK_VIEW_STATUS_LABELS.at_risk,
  off_track: ASK_VIEW_STATUS_LABELS.off_track,
  not_yet_measurable: ASK_VIEW_STATUS_LABELS.not_yet_measurable,
  overlay: 'Adjustment, not a separate commitment',
} as const satisfies Readonly<Record<AskViewThesisStatus | 'overlay', string>>;
export type AskViewCommitmentStatus = keyof typeof ASK_VIEW_COMMITMENT_STATUS_LABELS;
export type AskViewThesisReason =
  | 'adverse_signal'
  | 'amendment_required'
  | 'on_plan'
  | 'no_observation'
  | 'missing_plan'
  | 'pace_below_plan'
  | 'missing_commitment';
/** Text is server-authored; figures retain their engine receipts and appear only in text. */
export type AskViewThesisEvidence = Readonly<{
  kind: string;
  text: string;
  forecastText?: string;
  dependencyNote?: string;
  dependencyRef?: string;
  dependencyLabel?: string;
  figures: readonly SynergyFigure[];
}>;
export type AskViewThesisRow = Readonly<{
  address?: AskViewAddress;
  behind?: SynergyFigure | null;
  behindShare?: number | null;
  commitmentRef: string;
  label: string;
  owner?: string;
  assumption: string;
  valueDriver: string;
  inPlan?: boolean;
  measurementBasis?: import('../../figure-period-basis').FigurePeriodBasis;
  status: AskViewThesisStatus;
  reasonCode: AskViewThesisReason;
  reason: string;
  evidence: readonly AskViewThesisEvidence[];
}>;
export type AskViewThesisPillar = Readonly<{
  pillarRef: string;
  label: string;
  tally?: string;
  outsidePlanNote?: string;
  status: AskViewThesisStatus;
  rows: readonly AskViewThesisRow[];
}>;
export type AskViewThesisBlock = Readonly<{
  kind: 'thesis';
  stripLabel?: string;
  fineprint?: string;
  relatedOwnerNote?: string;
  pillars: readonly AskViewThesisPillar[];
}>;

export type AskViewDecisionState = keyof typeof ASK_VIEW_STATUS_LABELS;
export type AskViewDecisionRow = AskViewDecisionFinance & Readonly<{
  accountability?: AskViewDecisionAccountability;
  decisionId: string;
  title: string;
  ownerRole: string | null; // A governed role title; null reads "Nobody".
  /** Screen-only directory join (accountability remains in ownerRole); "Restricted" when withheld. */
  ownerDisplay?: string | null;
  ownerWithheld?: true; // #11468: the joined owner is withheld from this viewer.
  /** Reader date of the decision ("Apr 13, 2026"). */
  decidedOn: string;
  /** Days from the decision to the as-of (open) or to the day it closed (closed). */
  daysOpen: number;
  /** The ledger's own grade for the latest week, in reader words. Server-authored. */
  statusText: string;
  /** The run-rate the decision protects (its plan target), cited to the ledger row. */
  protectedValue: SynergyFigure | null;
  /** The run-rate that arrived at the latest month-end, from the latest snapshot. */
  arrivedValue: SynergyFigure | null;
  /** The snapshot's actualsSummary, naming the month-end and signal report. */
  measuredAs: string | null;
  /** A drill to the view that explains it (behind or warnings), or null. */
  address: AskViewAddress | null;
  scope?: Readonly<{ kind: 'synergy_commitment' | 'synergy_dependency'; ref: string }>;
  /** The status word for the row ("Amendment required", "Drifting · watch", "On track"). */
  statusWord?: string;
  /** Its detail, from the same typed grades ("2 warning signals fired · 16.7% below …"), or null. */
  statusDetail?: string | null;
  /** Arrived over target, clamped 0..1: a bar position only (never displayed). */
  arrivedShare?: number | null;
  /** 1-based index of the step card that carries this decision's figures, when one does. */
  stepIndex?: number | null;
}>;
export type AskViewDecisionGroup = Readonly<{
  state: AskViewDecisionState;
  label: (typeof ASK_VIEW_STATUS_LABELS)[AskViewDecisionState];
  rows: readonly AskViewDecisionRow[];
}>;
export type AskViewNextStep = Readonly<{
  publishedAction?: AskViewPublishedAction | null;
  next: string;
  why: string;
  /** Set when `why` cites a figure wider than the step's scope: the deal, with its label. */
  whyScope?: Readonly<{ kind: 'synergy_deal'; label: string }>;
  /** Governed figures the `why` cites, copied with the producer's bindings. */
  figures?: readonly SynergyFigure[];
  action: AskViewBandAction;
  /** The view the step comes from, opened by the row. */
  address: AskViewAddress;
  /** The selected open decision, another covered step, or an uncovered action. */
  kind?: 'push' | 'step' | 'new';
  /** The open decision this step moves, when one covers its scope (same typed scope). */
  decision?: AskViewDecisionFinance & Readonly<{
    owner?: string | null;
    accountability?: AskViewDecisionAccountability;
    decisionId: string;
    title: string;
    state: Exclude<AskViewDecisionState, 'closed'>;
    statusWord?: string;
    daysOpen: number;
    protectedValue: SynergyFigure | null;
    arrivedValue: SynergyFigure | null;
    arrivedShare: number | null;
  }> | null;
  /** Null from the v2 reader: the step's ask names its governed owner once (no 2nd owner line). */
  owner?: string | null;
  /** "No owner is on record for <label>." when the ask names nobody: own line, never the ask. */
  ownerGap?: string;
  /** By when, from the step's decision type ("the board pack"), or the named gap ("not set: …"). */
  by?: Readonly<{ text: string; isGap: boolean }> | null;
}>;
export type AskViewDecisionsBlock = Readonly<{
  kind: 'decisions';
  recoveryCommitted?: SynergyFigure | null;
  options?: AskViewPublishedOptions;
  recoveryScenario?: AskViewRecoveryScenario;
  review?: AskViewOperatingReview;
  orderLabel?: string;
  scopeCoverage?: AskViewDecisionScopeCoverage;
  openDecisions?: readonly Readonly<{
    row: AskViewDecisionRow; state: Exclude<AskViewDecisionState, 'closed'>;
  }>[];
  /** Always in the order On track, Drifting, Closed; an empty group is omitted. */
  groups: readonly AskViewDecisionGroup[];
  /**
   * v1: today's recommended steps that no open decision covers yet. v2 (when `fineprint` is set):
   * up to three steps in order: the push step, one other open decision's step, then a new step.
   */
  nextSteps: readonly AskViewNextStep[];
  /** v2: the as-of line every open row shares, said once at the top; null when rows differ. */
  fineprint?: string | null;
  /** One plain line when the calculated next move could not be read; the steps are the ledger's. */
  moveNote?: string;
  tableNote?: string;
}>;

/** `since`: the signals whose status changed between the two as-ofs, in governed words. */
export type AskViewTurnedBlock = Readonly<{
  kind: 'turned';
  presentation?: AskViewComparisonPresentation;
  /** Names both as-ofs, e.g. "Signals that changed (Aug 31 vs Jul 31, 2026)". */
  label: string;
  turned: readonly SynergyTurnedSignal[];
}>;

/** `verified`: the published ledger verification for the deal at the address's month. */
export type AskViewVerificationBlock = Readonly<{
  kind: 'verification';
  programOutcome?: AskViewProgramOutcome;
  presentation?: AskViewComparisonPresentation;
  verification: SynergyVerificationView;
}>;

/** One in-total commitment behind plan or late (SPEC §6.4). An absent figure is null, never 0. */
export type AskViewBehindRow = Readonly<{
  commitmentRef: string;
  label: string;
  owner: string;
  /** Reader dates ("Dec 31, 2026"); null reads "not supplied", never "on schedule". */
  committedDate: string | null;
  forecastDate: string | null;
  daysLate: SynergyFigure | null;
  /** Planned to date minus delivered to date; the shortfall, not the committed value. */
  behind: SynergyFigure | null;
  underwritten: SynergyFigure;
}>;

/** `behind`: rows by behind plan, largest first, the total once, one exposure line. */
export type AskViewBehindBlock = Readonly<{
  kind: 'behind';
  visual?: BehindVisual;
  rows: readonly AskViewBehindRow[];
  /** The sum of the rows' behind-plan figures; null when none is supplied. */
  total: SynergyFigure | null;
  /** The committed run-rate that depends on one named dependency, or null. */
  exposure: Readonly<{ label: string; figure: SynergyFigure }> | null;
  /** Commitments outside the deal total that are behind plan: a source line, never summed. */
  outsideTotal: readonly Readonly<{ commitmentRef?: string; label: string; behind: SynergyFigure }>[];
}>;

/** A commitment under a cause. Its behind-plan amount shows under its first cause only. */
export type AskViewCauseLeaf = Readonly<{
  commitmentRef: string;
  label: string;
  owner?: string;
  /** False for a commitment outside the deal total (an unresolved overlap). */
  inTotal: boolean;
  /** The signals fired on this commitment under this cause. */
  signals: SynergyFigure;
  /** Shown only under the commitment's first (highest-ranked) cause. */
  behind: SynergyFigure | null;
  /** The rank of the first cause, when the amount shows there instead. */
  alsoUnder: number | null;
}>;

/** One operational driver (SPEC §5 "Cause"), ranked by the behind plan of its commitments. */
export type AskViewCause = Readonly<{
  rank: number;
  name: string;
  /** Source identity of a dependency cause; labels never bind recommendation dollar priority. */
  dependencyRef?: string | null;
  /** Only when the cause has more than one leaf. */
  total: SynergyFigure | null;
  leaves: readonly AskViewCauseLeaf[];
}>;

/** `warnings`: the ranked cause tree; "Cause not identified" is the last cause when present. */
export type AskViewWarningsBlock = Readonly<{
  kind: 'warnings';
  visual?: WarningsVisual;
  causes: readonly AskViewCause[];
  /** Commitments with warnings and fired signals, counted separately (never one number). */
  commitmentCount: SynergyFigure;
  signalCount: SynergyFigure;
}>;

/** A governed view (SPEC §4). */
export type AskViewPayload = Readonly<{
  actionPlan?: import('./saved-action-plan').SavedActionPlanView;
  reconciliations?: readonly import('../contracts/synergy-reconciliation').SynergyReconciliation[];
  address: AskViewAddress;
  crumbs: readonly AskViewCrumb[];
  /** The verdict, one sentence. */
  headline: string;
  /** Zero to two sentences: the talk track. */
  talk: readonly string[];
  /** Null when no typed finding supports a recommendation. */
  band: AskViewBand | null;
  exhibit: AskViewExhibit;
  /** Two or three links onward. */
  drills: readonly AskViewDrill[];
  /** Three short questions from the view's top finding. */
  tryQuestions: readonly string[];
  missing: readonly AskViewMissing[];
}>;

/** One deal the selector offers (SPEC §2.4); parties and short ids are NEEDS DATA today. */
/**
 * One program the selector offers. `shortLabel` is the deal-lines catalog's declared short name
 * ("Phoenix"), absent when none is declared; the selector then shows `label`.
 */
export type AskViewDeal = Readonly<{
  dealId: string;
  label: string;
  shortLabel?: string;
  asOf: AskViewDay;
}>;
export type AskViewDeals = Readonly<{ deals: readonly AskViewDeal[] }>;

/** Server-declared availability for the caller and scoped, resolvable evidence addresses. */
export type AskViewAvailability = Readonly<{
  viewId: AskViewId;
  built: boolean;
  /** Display-ready explanation when the caller cannot open this view. */
  reason?: string;
}>;
export type AskViewNavigation = Readonly<{
  views: readonly AskViewAvailability[];
  commitmentDrills: readonly AskViewAddress[];
}>;
export const ASK_VIEW_NAVIGATION_PATH = 'v1/views/navigation';

/** The routes: one view, and the deal list the selector reads. */
export const ASK_VIEW_ROUTE = 'v1/views/:viewId';
export const ASK_VIEW_DEALS_PATH = 'v1/views/deals';

/**
 * A commitment's evidence drill (lead 2026-09-27): a bridge step, a behind-plan row or a cause
 * leaf opens that commitment's IT spend on the buyer tab, at the deal and as-of it was read at.
 * The commitment is its governed ref (a step's `memberRef`, a row's `commitmentRef`), never a label.
 */
export function askCommitmentEvidenceAddress(
  from: Pick<AskViewAddress, 'dealId' | 'asOf'>,
  commitmentId: string
): AskViewAddress {
  return {
    dealId: from.dealId,
    asOf: from.asOf,
    viewId: 'ev-it',
    params: { tab: 'buyer', commitmentId },
  };
}

/** `v1/views/<viewId>?dealId=&asOf=&<params>` (SPEC §2.1). */
export function askViewPath(address: AskViewAddress): string {
  const query = new URLSearchParams([
    ['dealId', address.dealId],
    ['asOf', address.asOf],
    ...Object.entries(address.params),
  ]).toString();
  return `v1/views/${address.viewId}?${query}`;
}
